/*
 * THE RECORD'S WAYS OF WRITING A DATE, a day count and a name.
 *
 * Small and pure, so every model in this folder words a date the same way and the
 * tests can pin "now" (ui.js reads the real clock, and ui.js is a .js file the
 * node test runner cannot import as a module). The house rules are the brief's:
 * "Sep 30" with the year only when it is not this year, ranges "Sep 27–30", one
 * 24-hour clock, sentence case.
 *
 * Every function that compares with today takes `now`, defaulting to the real
 * clock, so a test written on Oct 1 still passes in December.
 */

const MS_DAY = 86400000;

/** A Date, or null for anything that is not a usable instant. */
export function toDate(ts) {
  if (ts === null || ts === undefined || ts === "") return null;
  const d = ts instanceof Date ? ts : new Date(ts);
  return Number.isFinite(d.getTime()) ? d : null;
}

/*
 * CALENDAR days from `a` to `b`, midnight to midnight in the browser's own zone,
 * the same reading as src/actions/model.js daysBetween. An event at 23:50 last
 * night is one day ago, not zero: "parked 183 days" and "day 1" are both day
 * counts a person reads off a calendar, not elapsed hours.
 */
export function daysBetween(a, b) {
  const x = toDate(a);
  const y = toDate(b);
  if (!x || !y) return null;
  const day = (d) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.round((day(y) - day(x)) / MS_DAY);
}

/** `ts` plus `n` days, keeping the clock time. */
export function addDays(ts, n) {
  const d = toDate(ts);
  return d ? new Date(d.getTime() + n * MS_DAY) : null;
}

function fmt(d, opts, timeZone) {
  return d.toLocaleDateString("en-US", timeZone ? { ...opts, timeZone } : opts);
}

function yearOf(d, timeZone) {
  return fmt(d, { year: "numeric" }, timeZone);
}

/** "Sep 30", or "Mar 14, 2025" when the date is not in the current year. */
export function shortDate(ts, { now = new Date(), timeZone } = {}) {
  const d = toDate(ts);
  if (!d) return "";
  const opts = { month: "short", day: "numeric" };
  if (yearOf(d, timeZone) !== yearOf(toDate(now) || new Date(), timeZone)) opts.year = "numeric";
  return fmt(d, opts, timeZone);
}

/** "Jan 1, 2027": the year always shows, for dates whose year carries meaning. */
export function longDate(ts, { timeZone } = {}) {
  const d = toDate(ts);
  return d ? fmt(d, { month: "short", day: "numeric", year: "numeric" }, timeZone) : "";
}

/** "today", "yesterday", "tomorrow", or the short date. */
export function relativeDay(ts, { now = new Date(), timeZone } = {}) {
  const n = daysBetween(ts, now);
  if (n === null) return "";
  if (n === 0) return "today";
  if (n === 1) return "yesterday";
  if (n === -1) return "tomorrow";
  return shortDate(ts, { now, timeZone });
}

/** "14:05", one 24-hour clock everywhere on the record. */
export function clock(ts, { timeZone } = {}) {
  const d = toDate(ts);
  if (!d) return "";
  const opts = { hour: "2-digit", minute: "2-digit", hour12: false };
  return d.toLocaleTimeString("en-GB", timeZone ? { ...opts, timeZone } : opts);
}

/** "Sep 27–30", "Sep 28–Oct 1", a single day when both ends are one day. */
export function dateRange(from, to, { now = new Date(), timeZone } = {}) {
  const a = toDate(from);
  const b = toDate(to);
  if (!a || !b) return a ? shortDate(a, { now, timeZone }) : b ? shortDate(b, { now, timeZone }) : "";
  const [lo, hi] = a <= b ? [a, b] : [b, a];
  const loText = shortDate(lo, { now, timeZone });
  const hiText = shortDate(hi, { now, timeZone });
  if (loText === hiText) return loText;
  const sameMonth =
    fmt(lo, { month: "short", year: "numeric" }, timeZone) === fmt(hi, { month: "short", year: "numeric" }, timeZone);
  if (sameMonth) return `${loText}–${fmt(hi, { day: "numeric" }, timeZone)}`;
  return `${loText}–${hiText}`;
}

/** "1 day", "183 days": a count with its noun, never "1 days". */
export function plural(n, noun, many = `${noun}s`) {
  return `${n} ${n === 1 ? noun : many}`;
}

/** First name, else the whole name, else the address, else null. */
export function firstName(person) {
  if (!person) return null;
  const first = typeof person.first_name === "string" ? person.first_name.trim() : "";
  if (first) return first;
  return fullName(person);
}

/** "Shahar Bahat", else the address, else null. */
export function fullName(person) {
  if (!person) return null;
  const name = [person.first_name, person.last_name].filter((s) => typeof s === "string" && s.trim()).join(" ").trim();
  return name || (typeof person.email === "string" && person.email) || null;
}

/** "Shahar, Gali" / "Shahar, Gali and Shachar": a list as a person writes it. */
export function listWords(words) {
  const list = (words || []).filter(Boolean);
  if (list.length <= 2) return list.join(list.length === 2 ? " and " : "");
  return `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`;
}

/** An enum as words: "COMPUTER_NETWORK_SECURITY" → "computer network security". */
export function enumWords(value) {
  if (value === null || value === undefined) return "";
  return String(value).replace(/_/g, " ").trim().toLowerCase();
}

/** "Sentence case" for a label built from words. */
export function sentence(text) {
  const s = String(text || "").trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/** Compact money, the board's own reading: 30000 → "$30K", 3500 → "$3.5K". */
const SYMBOL = { USD: "$", EUR: "€", GBP: "£", ILS: "₪" };
export function moneyShort(value, currency = "USD") {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  const sym = SYMBOL[currency] ?? (currency ? "" : "$");
  if (Math.abs(n) >= 1e6) {
    const m = n / 1e6;
    return `${sym}${Number.isInteger(m) ? m : m.toFixed(1)}M`;
  }
  if (Math.abs(n) >= 1000) {
    const k = n / 1000;
    return `${sym}${Number.isInteger(k) ? k : k.toFixed(1)}K`;
  }
  return `${sym}${n}`;
}
